import { uniqBy } from 'lodash';
import { utils, writeFile } from 'xlsx';

import type { Event } from '../../../Data/types';
import { formatHeader } from '../formatHeader/formatHeader';
import { excludedFields, exportFieldOrder } from '../orderFields/orderFields';
import { parseEvent } from '../parseEvent/parseEvent';

const exportToXLSX = (filename: string, rows: Event[]) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const uniqueRows = uniqBy(rows, 'id') as any[];

  if (!uniqueRows || uniqueRows.length === 0) {
    console.warn('No data to export');
    return;
  }

  uniqueRows.forEach((row) => {
    if (row.eventDateTime) {
      row.start = row.eventDateTime.start;
      row.end = row.eventDateTime.end;
    }
    delete row.eventDateTime;
  });

  // Build headers
  const headers = exportFieldOrder
    .filter((key) => !excludedFields.includes(key))
    .flatMap((key) => {
      if (key === 'eventContact') return ['Event Contact'];
      if (key === 'created_by_user') return ['Entered By'];
      if (key === 'title') return ['Event Name'];
      if (key === 'timezone') return ['Time Zone'];
      if (key === 'hostingType') return ['Event Category'];
      if (key === 'isLocationExternal') return ['Location Type'];
      if (key === 'officeLocation') return ['Hosting Office'];
      if (key === 'externalLocation') return ['Venue Name and Location'];
      if (key === 'id') return ['System ID'];
      return formatHeader(key);
    });

  // Build data rows
  const data = uniqueRows.map((row) => {
    return exportFieldOrder
      .filter((key) => !excludedFields.includes(key))
      .flatMap((key) => {
        if (key === 'eventContact') {
          return (row.eventContact ?? []).map((c: { DisplayName: string }) => c.DisplayName || '');
        }
        if (key === 'created_by_user') {
          return row.created_by_user?.DisplayName || '';
        }
        if (key === 'hostingType') {
          return row.hostingType || '';
        }
        if (key === 'isLocationExternal') {
          return row.isLocationExternal ? 'External' : 'Internal';
        }
        if (key === 'externalLocation') {
          if (row.externalLocation) {
            return [row.externalLocation];
          }
          if (row.officeLocation && row.officeLocation.length > 0) {
            return [
              row.officeLocation
                .map((o: { address: string }) => o.address)
                .filter(Boolean)
                .join(', '),
            ];
          }
          return [''];
        }
        return parseEvent({ [key]: row[key] } as Event);
      });
  });

  // Combine into worksheet
  const worksheet = utils.aoa_to_sheet([headers, ...data]);

  // Create workbook and append sheet
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, 'Events');

  // Generate and trigger download
  writeFile(workbook, `${filename}.xlsx`);
};

export default exportToXLSX;
